import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { SharedLanguageService } from '@language';
import { BehaviorSubject } from 'rxjs';
import { ActionItem } from '../action-item/entities/action-item.entity';
import { ShIconModule } from '../icon/icon.module';
import { ImageViewerTranslations, ImageViewerTranslationsPartial } from './entities/image-viewer-translations.entity';

export * from './entities/image-viewer-translations.entity';

export enum ViewerActionsPosition {
    TOP_OVERLAY = '-sh-top',
    RIGHT_OVERLAY = '-sh-right',
    BOTTOM_OVERLAY = '-sh-bottom',
    LEFT_OVERLAY = '-sh-left',
    BOTTOM_EXTERNAL = '-sh-viewer-button'
}

/**
 *  Forked from https://github.com/deepakgonda/angular-image-viewer project
 *  Modified for usage in combination with Stylehaven
 */
@Component({
    standalone: true,
    selector: 'sh-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    imports: [CommonModule, ShIconModule]
})
export class ShImageViewerComponent implements OnInit {
    public readonly ViewerActionsPosition: typeof ViewerActionsPosition = ViewerActionsPosition;
    private readonly ctrlPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public style: any = { transform: '', msTransform: '', oTransform: '', webkitTransform: '' };
    public loading = true;
    public dragging = false;
    public draggable = true;
    public transition = false;
    public imageActionButtons!: ActionItem[];
    public _imageSources: string[] = [];

    protected scale = 1;
    protected rotation = 0;
    protected translateX = 0;
    protected translateY = 0;
    private prevX!: number;
    private prevY!: number;
    private hovered = false;

    private readonly defaultTranslations: ImageViewerTranslations = {
        zoomIn: 'image-viewer.zoomIn',
        zoomOut: 'image-viewer.zoomOut',
        rotateClockwise: 'image-viewer.rotateClockwise',
        rotateCounterClockwise: 'image-viewer.rotateClockwise',
        undo: 'image-viewer.undo',
        next: 'image-viewer.next',
        previous: 'image-viewer.previous'
    };

    public viewerTranslations: ImageViewerTranslations = this.defaultTranslations;

    @Input()
    set imageSources(imgInput: string[]) {
        this.reset();
        this._imageSources = imgInput;
    }

    @Input() fillSpace: boolean = false;

    @Input() actionsPosition: ViewerActionsPosition = ViewerActionsPosition.RIGHT_OVERLAY;

    @Input()
    index = 0;

    @Input()
    zoomFactor = 0.1;

    @Input()
    zoom = true;

    @Input()
    rotate = false;

    @Input() fullscreen: boolean = true;

    @Input()
    customActionItems: ActionItem[] = [];

    @Input() allowTransform: boolean = true;

    @Input() i18n: ImageViewerTranslationsPartial;

    @Output()
    indexChange: EventEmitter<number> = new EventEmitter();

    @ViewChild('image', { static: false }) image!: ElementRef;

    private readonly languageService: SharedLanguageService = inject(SharedLanguageService);

    ngOnInit() {
        if (this.fillSpace) {
            this.style.width = '100%';
        }

        this.initTranslations();
        this.initDragging();
        this.initImageButtons();
    }

    private initTranslations(): void {
        this.viewerTranslations = {
            zoomIn: this.languageService.translate(this.defaultTranslations.zoomIn),
            zoomOut: this.languageService.translate(this.defaultTranslations.zoomOut),
            rotateClockwise: this.languageService.translate(this.defaultTranslations.rotateClockwise),
            rotateCounterClockwise: this.languageService.translate(this.defaultTranslations.rotateCounterClockwise),
            undo: this.languageService.translate(this.defaultTranslations.undo),
            next: this.languageService.translate(this.defaultTranslations.next),
            previous: this.languageService.translate(this.defaultTranslations.previous),
            ...this.i18n
        };
    }

    protected initDragging() {
        this.draggable = this.zoom;
    }

    private initImageButtons() {
        const actionItems: ActionItem[] = [];

        if (this.zoom) {
            actionItems.push(...this.getZoomActionItems());
        }

        if (this.rotate) {
            actionItems.push(...this.getRotateActionItems());
        }

        if (this.zoom || this.rotate) {
            const reset: ActionItem = {
                label: this.viewerTranslations.undo,
                icon: 'undo',
                eventListener: this.reset.bind(this, true)
            };

            actionItems.push(reset);
        }

        if (this.customActionItems.length > 0) {
            actionItems.push(...this.customActionItems);
        }

        this.imageActionButtons = actionItems.reverse();
    }

    private getZoomActionItems(): ActionItem[] {
        const zoomIn: ActionItem = {
            label: this.viewerTranslations.zoomIn,
            icon: 'zoom-in',
            eventListener: this.zoomIn.bind(this)
        };

        const zoomOut: ActionItem = {
            label: this.viewerTranslations.zoomOut,
            icon: 'zoom-out',
            eventListener: this.zoomOut.bind(this)
        };

        return [zoomIn, zoomOut];
    }

    private getRotateActionItems(): ActionItem[] {
        const rotateClockwise: ActionItem = {
            label: this.viewerTranslations.rotateClockwise,
            icon: 'rotate-clockwise',
            eventListener: this.rotateClockwise.bind(this)
        };

        const rotateCounterClockwise: ActionItem = {
            label: this.viewerTranslations.rotateCounterClockwise,
            icon: 'rotate-ct-clockwise',
            eventListener: this.rotateCounterClockwise.bind(this)
        };

        return [rotateClockwise, rotateCounterClockwise];
    }

    @HostListener('window:keyup.ArrowRight', ['$event'])
    nextImage(event: Event) {
        if (this.canNavigate(event) && this.index < this._imageSources.length - 1) {
            this.loading = true;
            this.index++;
            this.triggerIndexBinding();
            this.reset();
        }
    }

    @HostListener('window:keyup.ArrowLeft', ['$event'])
    prevImage(event: Event) {
        if (this.canNavigate(event) && this.index > 0) {
            this.loading = true;
            this.index--;
            this.triggerIndexBinding();
            this.reset();
        }
    }

    @HostListener('window:keydown.Ctrl', ['$event']) onKeyDown() {
        this.ctrlPressed$.next(true);
    }

    @HostListener('window:keyup.Ctrl', ['$event']) onKeyUp() {
        this.ctrlPressed$.next(false);
    }

    @HostListener('window:blur', ['$event'])
    handleBlur(event: FocusEvent) {
        this.ctrlPressed$.next(false);
    }

    // Handle when page becomes hidden (e.g., user switches tabs)
    @HostListener('document:visibilitychange', [])
    onVisibilityChange() {
        if (document.hidden) {
            this.ctrlPressed$.next(false);
        }
    }

    zoomIn() {
        this.reFocus();
        this.transition = true;
        this.scale *= 1 + this.zoomFactor;
        this.updateStyle();
    }

    zoomOut() {
        this.reFocus();
        this.transition = true;
        if (this.scale > this.zoomFactor) {
            this.scale /= 1 + this.zoomFactor;
        }
        this.updateStyle();
    }

    scrollZoom(evt: WheelEvent): void {
        this.reFocus();
        if (this.zoom && (this.ctrlPressed$.value || evt.ctrlKey)) {
            evt.deltaY > 0 ? this.zoomOut() : this.zoomIn();
            evt.stopPropagation();
            evt.preventDefault();
        }
    }

    rotateClockwise(): void {
        this.transition = true;
        this.rotation += 90;

        this.onRotate();
    }

    rotateCounterClockwise(): void {
        this.transition = true;
        this.rotation -= 90;

        this.onRotate();
    }

    private onRotate(): void {
        if (!this.draggable || !this.zoom) {
            this.rescaleImgToFit();
        }
        this.updateStyle();
    }

    protected rescaleImgToFit(): void {
        const imgHeight: number = this.image.nativeElement.height;
        const imgWidth: number = this.image.nativeElement.width;
        const naturalHeight: number = this.image.nativeElement.naturalHeight;

        if (this.rotation % 180 === 0) {
            this.scale = 1;
        } else {
            const newScale: number = imgHeight / imgWidth;
            this.scale = imgHeight * newScale <= naturalHeight ? newScale : 1;
        }
    }

    onLoad(url: string): void {
        this.loading = false;
    }

    onLoadStart(url: string): void {
        this.loading = true;
    }

    imageNotFound(url: string): void {
        console.error('Image not found Url:', url);
    }

    onDragOver(evt: DragEvent): void {
        if (!this.draggable || !this.dragging) {
            return;
        }
        evt.preventDefault();
        this.transition = false;
        this.translateX += evt.clientX - this.prevX;
        this.translateY += evt.clientY - this.prevY;
        this.prevX = evt.clientX;
        this.prevY = evt.clientY;
        this.updateStyle();
    }

    onDragStart(evt: DragEvent): void {
        this.dragging = true;
        this.prevX = evt.clientX;
        this.prevY = evt.clientY;
    }

    onDragEnd(evt: any): void {
        this.dragging = false;
    }

    triggerIndexBinding(): void {
        this.indexChange.emit(this.index);
    }

    reset(transition: boolean = false): void {
        this.transition = transition;
        this.scale = 1;

        this.rotation = this.getClosestMultipleOf360(this.rotation);

        this.reFocus();
        this.updateStyle();
    }

    reFocus(): void {
        this.translateX = 0;
        this.translateY = 0;
    }

    private getClosestMultipleOf360(angle: number): number {
        const horizontalAligned = angle + (angle % 180);
        const rotateTo = horizontalAligned - (horizontalAligned % 360);
        return rotateTo;
    }

    @HostListener('mouseover')
    onMouseOver(): void {
        this.hovered = true;
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        this.hovered = false;
    }

    private canNavigate(event: any): boolean {
        return event == null || this.hovered;
    }

    protected updateStyle(): void {
        this.style.transform = `translate(${this.translateX}px, ${this.translateY}px) rotate(${this.rotation}deg) scale(${this.scale})`;
        this.style.msTransform = this.style.transform;
        this.style.webkitTransform = this.style.transform;
        this.style.oTransform = this.style.transform;
    }
}
